import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { thunks } from "@/lib/redux/thunks"
import { blogActions } from "@/lib/redux/features/blog/reduce"
import { useSearchParams } from "next/navigation"
import { useEffect, useRef } from "react"
import { getBlogListQueryParams } from "@/utils/blog/getBlogListQueryParams"
import { getCachedBlogCategory, setCachedBlogCategory } from "@/utils/blog/blogCategoryCache"

export const useBlogs = (category: string = "TRAVEL") => {
  const searchParams = useSearchParams()
  const dispatch = useAppDispatch()
  const blogListState = useAppSelector(state => state.blog.blog_list)
  const currentCategoryRef = useRef<string>(searchParams.get("category") || "TRAVEL")

  const refetchBlogList = (filter?: any) => {
    dispatch(thunks.blog_list({
      params: getBlogListQueryParams({
        ...blogListState.filter,
        ...filter,
      }),
    }))
  }

  useEffect(() => {
    // Always fetch data when category changes or on initial load
    if (currentCategoryRef.current !== category) {
      currentCategoryRef.current = category

      // Already fetched this category earlier in this tab — reuse it, skip the API call.
      const cached = getCachedBlogCategory(category)
      if (cached) {
        dispatch(blogActions.setBlogsData({ data: cached }))
        return
      }

      dispatch(thunks.blog_list({
        params: getBlogListQueryParams({ category }),
      }))
    }
  }, [category, dispatch, blogListState.called])

  // Filter data by category from the current state
  const filteredData = blogListState.data?.filter(blog => blog.category === category) || []

  // Cache whatever ends up loaded for the active category (SSR-hydrated or freshly fetched),
  // so switching away and back doesn't re-hit the API. Tab-scoped: sessionStorage clears on tab close.
  useEffect(() => {
    if (filteredData.length) {
      setCachedBlogCategory(category, filteredData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogListState.data])

  return {
    ...blogListState,
    data: filteredData,
    refetch: refetchBlogList
  }
}